import { FileText } from '@lucide/vue';
import {
  AddCircleIcon,
  BoardIcon,
  CalendarClockIcon,
  DatabaseMultipleIcon,
  SettingsIcon,
} from '@blpde/nx-icons';
import type { NxCommand } from '@blpde/nx-ui';
import { requestLocalLayoutEditor } from '@blpde/connect';
import {
  erpDefinitionsLoaded,
  erpListRoute,
  loadErpDefinitions,
  masterdataCommandTargets,
  purchaseCommandTargets,
  salesCreateCommandTargets,
} from '@erp/connect/shell';
import type { Router } from 'vue-router';
import { appLocationCommand } from './appCommand';
import { buildTasksConnectCommands } from './erpTasksCommands';
import type { CommandFactory, ModuleCommandContext } from './commands';

/** A requirement the account meets — or none at all, or no check to ask. */
function permitted(ctx: ModuleCommandContext, requirement: string | undefined): boolean {
  return !requirement || !ctx.can || ctx.can(requirement);
}

/**
 * Jumps to a resource's list. The path is the ERP route table's, so the jump
 * waits for the definitions (the shell starts their load at start-up).
 */
function goToList(router: Router, resource: string): void {
  void loadErpDefinitions().then(() => router.push(`/erp${erpListRoute(resource).path}`));
}

/**
 * One jump per Stammdaten cockpit tile — „ERP: Stammdaten › Kunden“
 * (I-2348). Derived from the cockpit's own tiles (served with the ERP's
 * definitions) through `@erp/connect/shell`, so a new tile brings its command
 * along; a tile whose screen the account may not read is left out. There are none
 * before the definitions are loaded — the shell rebuilds the commands then.
 */
export function buildMasterdataCommands(ctx: ModuleCommandContext): NxCommand[] {
  const { router, t, appLabel: erp } = ctx;
  const masterdata = t('masterdata.cockpit.title');
  return masterdataCommandTargets()
    .filter((target) => permitted(ctx, target.requiredPermissions))
    .map((target) => {
      const href = `/erp${target.path}`;
      return {
        id: `erp.masterdata.${target.id}`,
        label: `${erp}: ${masterdata} › ${t(target.labelKey)}`,
        group: erp,
        icon: DatabaseMultipleIcon,
        app: 'erp',
        category: 'navigation',
        keywords: [
          target.id,
          'stammdaten',
          'masterdata',
          t(target.domainLabelKey),
          t(target.subareaLabelKey),
        ],
        href,
        run: () => {
          void router.push(href);
        },
      } satisfies NxCommand;
    });
}

/**
 * „Neues Angebot / Neuer Auftrag / Neue Rechnung“ (I-2348): opens the list with
 * the intent `?new=<kind>`, and the list opens its create dialog — the one place
 * a sales document is created (the customer is picked there). Offered only with
 * the create right the list's button asks for.
 */
export function buildSalesCreateCommands(ctx: ModuleCommandContext): NxCommand[] {
  const { router, t, appLabel: erp } = ctx;
  return salesCreateCommandTargets()
    .filter((target) => permitted(ctx, target.requiredPermissions))
    .map((target) =>
      appLocationCommand(router, {
        id: `erp.sales.${target.kind}.new`,
        app: 'erp',
        group: erp,
        label: `${erp}: ${t(target.labelKey)}`,
        href: `/erp${target.path}?new=${target.kind}`,
        icon: AddCircleIcon,
        keywords: ['neu', 'new', 'anlegen', 'create', 'erstellen', 'vertrieb', 'sales'],
      })
    );
}

/** Derived from the purchase registry, including when the launcher hides its leaves. */
export function buildPurchaseCommands(ctx: ModuleCommandContext): NxCommand[] {
  const { router, t, appLabel: erp } = ctx;
  return purchaseCommandTargets()
    .filter((target) => permitted(ctx, target.requiredPermissions))
    .map((target) =>
      appLocationCommand(router, {
        id: `erp.purchase.${target.id}`,
        app: 'erp',
        group: erp,
        label: `${erp}: ${t('menu.areas.purchasing')} › ${t(target.labelKey)}`,
        href: `/erp${target.path}`,
        icon: FileText,
        category: 'navigation',
        keywords: ['einkauf', 'beschaffung', 'purchase', 'purchasing', target.id],
      })
    );
}

/**
 * erp's command-palette contribution — injected into the shell registry via
 * its AppEntry. The shell already auto-adds an "open ERP" command; these are
 * the deeper jumps into erp's surfaces. ERP's routes and navbar come from its
 * app module (frontend/erp/src/connect, I-1786); the commands stay here so the
 * palette offers them before ERP has been opened.
 */
export const erpCommands: CommandFactory = (ctx) => {
  const { router, t, appLabel: erp } = ctx;
  // The tasks entries and the sales lists take their captions (`nav.*`,
  // `menu.entries.*`) from the ERP's served texts (BLP/nx#3257): they join once
  // the definitions are in, as the Stammdaten targets do — the palette rebuilds
  // then (App.vue watches `erpDefinitionsLoaded`) and never shows a raw key.
  const served = erpDefinitionsLoaded.value;
  const tasks = served
    ? buildTasksConnectCommands({
        router,
        t,
        groupLabel: `${erp}: ${t('forge.brand')}`,
      })
    : [];
  return [
    {
      id: 'erp.dashboard',
      label: `${erp}: ${t('shell.nav.dashboard')}`,
      group: erp,
      icon: BoardIcon,
      keywords: ['dashboard', 'cockpit', 'auswertung', 'kpi'],
      run: () => {
        void router.push('/erp/dashboard');
      },
    },
    // DEV-only: open the current ERP screen's local layout editor from the
    // command palette (⌘K on macOS / Ctrl+K on Windows → type "Layout"). If
    // the screen cannot handle the local request, fall back to the popout
    // designer. Stripped from production builds.
    ...(import.meta.env.DEV
      ? [
          {
            id: 'erp.layoutEditor',
            label: `${erp}: ${t('shell.command.layoutEditor')}`,
            group: erp,
            icon: SettingsIcon,
            keywords: [
              'layout',
              'editor',
              'designer',
              'dev',
              'devmode',
              'entwicklung',
              'entwicklungsmodus',
              'devkeys',
            ],
            run: () => {
              if (requestLocalLayoutEditor({ source: 'command-palette' })) return;

              // Keep the host route alive. A named designer window can stick to
              // the current tab after navigation, which destroys the target
              // window and makes snapshot requests impossible. Resolved by path:
              // ERP's route names only exist once ERP has been loaded.
              window.open(
                router.resolve('/erp/nx/layout-designer').href,
                '_blank',
                'width=1100,height=900'
              );
            },
          },
        ]
      : []),
    ...tasks,
    ...(served
      ? [
        {
          id: 'erp.quotes',
          label: `${erp}: ${t('menu.entries.quotes')}`,
          group: erp,
          icon: FileText,
          keywords: ['quotes', 'angebote', 'sales', 'vertrieb'],
          run: () => {
            goToList(router, 'salesQuotes');
          },
        },
        {
          id: 'erp.orders',
          label: `${erp}: ${t('menu.entries.orders')}`,
          group: erp,
          icon: FileText,
          keywords: ['orders', 'auftraege', 'aufträge', 'sales', 'vertrieb'],
          run: () => {
            goToList(router, 'salesOrders');
          },
        },
        {
          id: 'erp.invoices',
          label: `${erp}: ${t('menu.entries.invoices')}`,
          group: erp,
          icon: FileText,
          keywords: ['invoices', 'rechnungen', 'sales', 'vertrieb'],
          run: () => {
            goToList(router, 'salesInvoices');
          },
        },
        ]
      : []),
    {
      // B1 (#2175) — the personnel desk's Übersicht over one employee's month.
      // In the palette rather than only in the nav because the desk reaches it
      // many times a day and the menu path is three clicks; the keywords carry
      // the words a user actually types („zeiten", „stempel"), not only the
      // screen's own label, which is the generic „Übersicht".
      //
      // The palette does NOT gate: the route does (`attendance_events.read`),
      // and a jump into a route the user may not enter is refused there — the
      // same deal every other entry here has.
      id: 'erp.timemanagement.attendanceReview',
      label: `${erp}: ${t('shell.command.attendanceReview')}`,
      group: erp,
      icon: CalendarClockIcon,
      keywords: [
        'zeitwirtschaft',
        'uebersicht',
        'übersicht',
        'attendance',
        'zeiten',
        'stempel',
        'stempelungen',
        'monat',
        'mitarbeiter',
      ],
      run: () => {
        void router.push('/erp/timemanagement/attendance-review');
      },
    },
    // Kunden, Lieferanten and Nummernkreise used to be typed out here; they are
    // cockpit tiles and come from there now, with every other tile.
    ...buildMasterdataCommands(ctx),
    ...buildSalesCreateCommands(ctx),
    ...buildPurchaseCommands(ctx),
  ];
};
